// PAGE RANKING — the one place that decides WHICH pages Otto offers, in what order, and how many.
//
// The problem it exists for is structural, not incidental: this platform runs three parallel hierarchies
// that reuse each other's page names. The project hub and the asset hub BOTH have Regions / Sites /
// Buildings; Assets appear under both the asset hub and the image hub; there are three dashboards (FCA,
// Asset, Image). Page names are therefore NOT unique, and every seam that answers "which page did you
// mean?" by listing candidates gets longer with each page registered — until a bare "show me buildings"
// offers six buttons and the assistant is worse than a menu.
//
// So candidates are PRUNED before they are ever offered, by four rules applied in this order:
//
//   1. NAMING IS AN ANSWER.  A target the utterance names exactly (its label or a synonym, as a whole
//      phrase) outranks everything. Registering more pages must never add options to an utterance that was
//      already unambiguous — this is the rule that guarantees it.
//   2. FAMILY.  A page in the CURRENT page's family beats one outside it. On the Asset Dashboard,
//      "buildings" means asset buildings; from the FCA Dashboard it means the project's. This resolves most
//      collisions SILENTLY — the best kind of disambiguation is the question never asked.
//   3. TIER.  A page Otto can act on (operable) beats one it merely understands, beats one it can only
//      open. Offering the surface where the request can actually be carried out is more useful than one
//      that merely shares the noun.
//   4. CATALOG ORDER.  Stable tiebreak, so the same question always renders the same way.
//
// ...and then CAPPED. Beyond three buttons a choice card stops being a clarification and becomes a
// directory, so the tail is folded into an explicit overflow the caller can offer as "somewhere else".
// Capping silently would be the worse failure — it would read as "those are all the options".
//
// Deterministic frontend code + registry data, never a prompt: the same rule as every other ambiguity
// decision in Otto (the routing demotion, the cross-capability clarify, the capability redirect), and for
// the same reason — the frontend is the only layer holding the live, permission-scoped candidate sets.
import { normalizeText } from "../../core/normalizeText";
import { familyOf, tierOf } from "./pageManifest";

// Beyond this many, a choice card is a directory. Three fits one line on the narrow chat panel and matches
// the widest question Otto already asks (the Buildings 3-way page choice).
export const MAX_PAGE_CHOICES = 3;

// HOW SPECIFICALLY does `utterance` name this target? The number of words in the LONGEST of its names
// (label or synonym) that appears in the utterance as a whole phrase; 0 when it names the target at all.
//
// A boolean is not enough, because short synonyms are subsets of long labels: "open the fca dashboard"
// contains both "fca dashboard" (the FCA Dashboard's label, 2 words) AND "dashboard" (the Asset Dashboard's
// synonym, 1 word), so a yes/no test says the user named BOTH and the tie survives — which is how a request
// naming one page can be answered with another. Specificity is the tiebreak, the same principle
// groundDestination already applies when it ranks exact matches above contains-matches.
export const nameMatchStrength = (utterance, target) => {
    const norm = ` ${normalizeText(utterance)} `;
    if (!norm.trim() || !target) return 0;
    const names = [normalizeText(target.label), ...((target.synonyms || []).map(normalizeText))].filter(Boolean);
    let best = 0;
    names.forEach(n => {
        if (norm.includes(` ${n} `)) best = Math.max(best, n.split(" ").filter(Boolean).length);
    });
    return best;
};

// Does `utterance` name this target at all? The same whole-word rule pagesNamedIn and groundDestination
// use, so "naming" means one thing across Otto.
export const targetNamedIn = (utterance, target) => nameMatchStrength(utterance, target) > 0;

// Rank candidates best-first for THIS mount. `page` is the current page key (supplies the family), and
// `utterance` the user's raw text (supplies the naming signal); both optional — with neither, the order is
// tier then catalog order, which is still better than raw catalog order and never worse.
//
// Pure and STABLE: equal-scoring targets keep their incoming order (the catalog's), so a re-render can't
// reshuffle the buttons under the user.
export const rankPageTargets = (targets, { page, utterance } = {}) => {
    const here = familyOf(page);
    const scored = (targets || []).filter(Boolean).map((target, index) => {
        // Naming dominates (and MORE specific naming dominates less specific), then family, then tier.
        const named = utterance ? nameMatchStrength(utterance, target) : 0;
        const sameFamily = !!here && familyOf(target.key) === here;
        return { target, index, score: named * 1000 + (sameFamily ? 100 : 0) + tierOf(target.key) };
    });
    scored.sort((a, b) => b.score - a.score || a.index - b.index);
    return scored.map(s => s.target);
};

// The single WINNER among candidates, or null when they genuinely tie and a question is owed. Used by seams
// that must either act or ask (nav grounding), as opposed to those that only order a list.
//
// Order matters and is the whole point: a candidate named MORE specifically than every other wins outright,
// BEFORE family is considered — otherwise standing on an assets page would answer "open the fca dashboard"
// with the asset dashboard, i.e. override an explicit request with a contextual guess.
export const pickPageTarget = (candidates, { page, utterance } = {}) => {
    const list = (candidates || []).filter(Boolean);
    if (list.length < 2) return list[0] || null;
    if (utterance) {
        const strengths = list.map(t => nameMatchStrength(utterance, t));
        const best = Math.max(...strengths);
        if (best > 0 && strengths.filter(s => s === best).length === 1) return list[strengths.indexOf(best)];
    }
    const inFamily = narrowToFamily(list, page);
    return inFamily.length === 1 ? inFamily[0] : null;
};

// Narrow to the CURRENT family, but only when that leaves something — a family filter that empties the
// list has told us nothing, and answering "nowhere" to a request Otto could serve from another family is
// worse than crossing families. Returns the input unchanged when the page has no known family.
export const narrowToFamily = (targets, page) => {
    const here = familyOf(page);
    if (!here) return targets || [];
    const inFamily = (targets || []).filter(t => t && familyOf(t.key) === here);
    return inFamily.length ? inFamily : targets || [];
};

// Cap a ranked list for display: { shown, overflow }. `overflow` is what did NOT fit — non-empty means the
// caller must say so rather than presenting `shown` as the complete set.
export const capPageChoices = (ranked, max = MAX_PAGE_CHOICES) => {
    const list = (ranked || []).filter(Boolean);
    return list.length <= max
        ? { shown: list, overflow: [] }
        : { shown: list.slice(0, max), overflow: list.slice(max) };
};

// The full pipeline for a set of candidate destinations: rank for this mount, then cap. Seams that offer
// PAGES call this so they can't disagree about order or length.
export const rankAndCap = (targets, { page, utterance, max } = {}) =>
    capPageChoices(rankPageTargets(targets, { page, utterance }), max);
